// Packages
import { useCallback, useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

// Models
import useModels from '../../../models';

// Helpers
import useHelpers from '../../../helpers';

// Actions
import useActions from '../../../api/actions';

// Components
import useComponents from '../../../views/components';

const useMFA = () => {
  const [isResending, setIsResending] = useState(false);
  const hasSentCode = useRef(false);

  // Helpers
  const { useQuickFunctions } = useHelpers();
  const { useValidators, useNavigation, useToast } = useQuickFunctions();
  const { mfaValidatorSchema } = useValidators();
  const { navigateTo } = useNavigation();
  const { success, error: toastError } = useToast();

  // Components
  const { Toast } = useComponents();

  // Actions
  const { dispatch, useAuthActions } = useActions();
  const { actLogin, actSendMfaToken, actClearMfaPending } = useAuthActions();

  // Models
  const { useSelectors } = useModels();
  const { useSelector, useAuthSelectors } = useSelectors();
  const { mfaPending } = useAuthSelectors();

  const { email, password, timestamp } = useSelector(mfaPending);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: 'all',
    resolver: yupResolver(mfaValidatorSchema),
    defaultValues: {
      mfa_code: '',
    },
  });

  // Check if credentials exist and are not expired (10 minutes)
  useEffect(() => {
    const TEN_MINUTES = 10 * 60 * 1000;
    
    if (!email || !password || !timestamp) {
      toastError(<Toast text="Sesión expirada. Por favor inicie sesión nuevamente." />, {
        toastId: 0,
      });
      navigateTo('/');
      return;
    }

    const isExpired = Date.now() - timestamp > TEN_MINUTES;
    if (isExpired) {
      toastError(<Toast text="Sesión expirada. Por favor inicie sesión nuevamente." />, {
        toastId: 0,
      });
      dispatch(actClearMfaPending());
      navigateTo('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, password, timestamp]); // Only depend on the actual data

  // Auto-send MFA token on mount
  useEffect(() => {
    if (email && !hasSentCode.current) {
      hasSentCode.current = true;
      
      const onSuccess = () => {
        success(<Toast text="Código enviado a tu correo electrónico" />, {
          toastId: 0,
        });
      };

      const onError = (error) => {
        toastError(<Toast text="Error al enviar el código. Por favor intente nuevamente." />, {
          toastId: 0,
        });
        hasSentCode.current = false; // Reset on error so user can try again
      };

      actSendMfaToken({ email }, onSuccess, onError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]); // Only depend on email

  const onSubmit = useCallback(
    ({ mfa_code }) => {
      const onSuccessLogin = (data) => {
        success(<Toast text="Inicio de sesión exitoso" />, {
          toastId: 0,
        });
        dispatch(actClearMfaPending());
        navigateTo('/dashboard');
      };

      const onErrorLogin = (error) => {
        toastError(<Toast text="Código MFA inválido o expirado" />, {
          toastId: 0,
        });
      };

      dispatch(actLogin({ email, password, mfa_token: mfa_code }, onSuccessLogin, onErrorLogin));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [email, password] // Only depend on the data we're sending
  );

  const handleResend = useCallback(() => {
    if (isResending) return;

    setIsResending(true);

    const onSuccess = () => {
      success(<Toast text="Código reenviado a tu correo electrónico" />, {
        toastId: 0,
      });
      setTimeout(() => setIsResending(false), 60000); // Throttle for 60 seconds
    };

    const onError = (error) => {
      toastError(<Toast text="Error al reenviar el código. Por favor intente nuevamente." />, {
        toastId: 0,
      });
      setIsResending(false);
    };

    actSendMfaToken({ email }, onSuccess, onError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, isResending]); // Only depend on email and isResending

  const getMaskedEmail = useCallback(() => {
    if (!email) return '';
    const [localPart, domain] = email.split('@');
    if (localPart.length <= 2) return email;
    const maskedLocal = localPart[0] + '*'.repeat(localPart.length - 2) + localPart[localPart.length - 1];
    return `${maskedLocal}@${domain}`;
  }, [email]);

  return {
    control,
    handleSubmit,
    onSubmit,
    handleResend,
    isResending,
    getMaskedEmail,
    errors,
  };
};

export default useMFA;
